import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../../component/Header";
import Footer from "../../component/Footer";
import { useForm } from "react-hook-form";
import axios from "../../helper/axios";

const inputClass =
	"w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 shadow-sm bg-white text-black";

export default function ResetPassword() {
	const [searchParams] = useSearchParams();
	const token = useMemo(
		() => (searchParams.get("token") || searchParams.get("t") || "").trim(),
		[searchParams]
	);

	const [loader, setLoader] = useState(false);
	const [msg, setMsg] = useState("");
	const [msgIsSuccess, setMsgIsSuccess] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const [passwordUpdated, setPasswordUpdated] = useState(false);

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: {
			email: "",
			password: "",
			confirmPassword: "",
		},
	});

	const passwordValue = watch("password");

	const requestReset = async (datax) => {
		try {
			setMsg("");
			setMsgIsSuccess(false);
			setLoader(true);
			const res = await axios.post("/resetpassword", { email: datax.email });
			if (res.status === 200 || res.status === 202) {
				setEmailSent(true);
				setMsgIsSuccess(true);
			} else {
				setMsg("Diçka shkoi keq. Provoni përsëri.");
			}
		} catch (err) {
			setMsg("Diçka shkoi keq. Provoni përsëri.");
		} finally {
			setLoader(false);
		}
	};

	const submitNewPassword = async (datax) => {
		try {
			setMsg("");
			const trimmedLen = (datax.password || "").replace(/\s/g, "").length;
			if (trimmedLen < 6) {
				setMsg("Fjalëkalimi kërkon minimumi 6 karaktere");
				return;
			}
			if (!token) {
				setMsg("Linku i rivendosjes është i pavlefshëm.");
				return;
			}

			setLoader(true);
			const res = await axios.post("/newpassword", {
				password: datax.password,
				token,
			});

			if (res.status === 200) {
				setPasswordUpdated(true);
			} else {
				setMsg(res.data?.msg || "Diçka shkoi keq. Provoni përsëri.");
			}
		} catch (err) {
			setMsg(err.response?.data?.msg || "Diçka shkoi keq. Provoni përsëri.");
		} finally {
			setLoader(false);
		}
	};

	const title = passwordUpdated
		? "Fjalëkalimi u Ndryshua"
		: token
			? "Vendosni Fjalëkalimin e Ri"
			: "Rivendosni Fjalëkalimin";

	return (
		<div className="min-h-screen bg-white flex flex-col">
			<Header className="bg-white text-black" />
			<main className="flex-grow flex items-center justify-center px-4 pt-20 pb-10">
				<div className="w-full max-w-4xl bg-white backdrop-blur-lg shadow-[0_8px_30px_rgba(0,0,0,0.08)] rounded-2xl p-8 sm:p-12 mt-24">
					<h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center text-black">
						{title}
					</h2>

					{loader && (
						<div className="flex justify-center my-4">
							<div className="h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
						</div>
					)}

					{passwordUpdated && (
						<div className="space-y-6 text-center">
							<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
								<svg className="h-7 w-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
								</svg>
							</div>
							<p className="text-green-600 text-sm sm:text-base">
								Fjalëkalimi u ndryshua me sukses. Ju lutem kyquni tani.
							</p>
							<Link
								to="/login"
								className="inline-block px-6 py-3 bg-black hover:bg-neutral-800 transition text-white rounded-xl font-semibold shadow-md"
							>
								Kyçuni
							</Link>
						</div>
					)}

					{!passwordUpdated && token && (
						<form onSubmit={handleSubmit(submitNewPassword)} className="space-y-5">
							<p className="text-center text-sm text-gray-600">
								Vendosni fjalëkalimin e ri për llogarinë tuaj.
							</p>
							<div>
								<input
									type="password"
									placeholder="Fjalëkalimi i Ri"
									{...register("password", { required: true })}
									className={inputClass}
								/>
								{errors.password && (
									<p className="text-red-600 text-sm mt-1">Fjalëkalimi është i detyrueshëm</p>
								)}
							</div>
							<div>
								<input
									type="password"
									placeholder="Përsëritni Fjalëkalimin"
									{...register("confirmPassword", {
										required: true,
										validate: (value) => value === passwordValue || "Fjalëkalimet nuk përputhen",
									})}
									className={inputClass}
								/>
								{errors.confirmPassword && (
									<p className="text-red-600 text-sm mt-1">
										{errors.confirmPassword.message || "Përsëritni fjalëkalimin"}
									</p>
								)}
							</div>
							{msg && <p className="text-red-600 text-center text-sm">{msg}</p>}
							<button
								type="submit"
								className="w-full bg-black hover:bg-neutral-800 transition text-white py-3 rounded-xl font-semibold shadow-md"
								disabled={loader}
							>
								Përditëso Fjalëkalimin
							</button>
							<Link to="/reset-password" className="block w-full text-center text-sm text-gray-600 hover:underline">
								Kërkoni një link të ri
							</Link>
						</form>
					)}

					{!passwordUpdated && !token && emailSent && (
						<div className="space-y-6 text-center">
							<p className="text-green-600 text-sm sm:text-base">
								Nëse emaili ekziston, do të merrni një link për rivendosjen e fjalëkalimit. Hapni emailin dhe klikoni linkun.
							</p>
							<button
								type="button"
								className="text-sm text-gray-600 hover:underline"
								onClick={() => {
									setEmailSent(false);
									setMsg("");
								}}
							>
								Dërgo përsëri
							</button>
							<Link to="/login" className="block text-sm text-gray-600 hover:underline">
								Kthehu te kyçja
							</Link>
						</div>
					)}

					{!passwordUpdated && !token && !emailSent && (
						<form onSubmit={handleSubmit(requestReset)} className="space-y-5">
							<div>
								<input
									type="email"
									placeholder="Email"
									{...register("email", { required: true })}
									className={inputClass}
								/>
								{errors.email && <p className="text-red-600 text-sm mt-1">Email është i detyrueshëm</p>}
							</div>
							{msg && (
								<p className={`text-center text-sm ${msgIsSuccess ? "text-green-600" : "text-red-600"}`}>
									{msg}
								</p>
							)}
							<button
								type="submit"
								className="w-full bg-black hover:bg-neutral-800 transition text-white py-3 rounded-xl font-semibold shadow-md"
								disabled={loader}
							>
								Dërgo linkun e rivendosjes
							</button>
							<Link to="/login" className="block w-full text-center text-sm text-gray-600 hover:underline">
								Kthehu te kyçja
							</Link>
						</form>
					)}
				</div>
			</main>
			<Footer className="bg-white text-black" />
		</div>
	);
}
